import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { useDatePreferences } from '../hooks/useDatePreferences'
import {
  Layers,
  ToggleLeft,
  ToggleRight,
  Shield,
  ArrowUp,
  History,
  Calendar,
  AlertTriangle,
  X,
  Check,
  ChevronDown,
  ChevronUp,
  GitBranch,
  ArrowRight,
  Clock,
  BarChart2,
  Download,
  Upload,
  FileJson,
} from 'lucide-react'

interface Framework {
  id: string
  name: string
  display_name: string
  description: string | null
  version: string | null
  enabled: number
  effective_date: number | null
  sunset_date: number | null
  superseded_by: string | null
  superseding_framework_name: string | null
  superseding_framework_version: string | null
  base_framework_name: string | null
  is_latest: number
  migration_notes: string | null
  upgrade_available: boolean
  control_count: number
  created_at: number
}

interface FrameworkVersion {
  id: string
  name: string
  display_name: string
  version: string | null
  enabled: number
  effective_date: number | null
  sunset_date: number | null
  is_latest: number
  control_count: number
  compliant_count: number
  created_at: number
}

export default function Frameworks() {
  const { user } = useAuth()
  const { formatDate } = useDatePreferences()
  const [frameworks, setFrameworks] = useState<Framework[]>([])
  const [loading, setLoading] = useState(true)
  const [toggling, setToggling] = useState<string | null>(null)
  const [expandedVersions, setExpandedVersions] = useState<string | null>(null)
  const [versions, setVersions] = useState<FrameworkVersion[]>([])
  const [loadingVersions, setLoadingVersions] = useState(false)

  // Modal states
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [showMigrateModal, setShowMigrateModal] = useState(false)
  const [selectedFramework, setSelectedFramework] = useState<Framework | null>(null)

  // Upgrade form
  const [upgradeForm, setUpgradeForm] = useState({
    new_version: '',
    new_display_name: '',
    migration_notes: '',
    copy_controls: true,
  })
  const [upgrading, setUpgrading] = useState(false)

  // Migrate form
  const [migrateForm, setMigrateForm] = useState({
    copy_evidence: true,
    copy_policies: true,
  })
  const [migrating, setMigrating] = useState(false)

  // Import/Export states
  const [showImportModal, setShowImportModal] = useState(false)
  const [importing, setImporting] = useState(false)
  const [exporting, setExporting] = useState<string | null>(null)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importMode, setImportMode] = useState<'create_new' | 'merge' | 'replace'>('create_new')
  const [importResult, setImportResult] = useState<{
    success: boolean
    message: string
    statistics?: {
      controls_created: number
      controls_updated: number
      controls_skipped: number
    }
  } | null>(null)

  async function fetchFrameworks() {
    try {
      const res = await api.getFrameworks()
      if (res.success) {
        setFrameworks(res.frameworks)
      }
    } catch (err) {
      console.error('Failed to load frameworks:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchFrameworks()
  }, [])

  const handleToggle = async (framework: Framework) => {
    if (user?.role !== 'admin') return

    setToggling(framework.id)
    try {
      const res = await api.toggleFramework(framework.id, !framework.enabled)
      if (res.success) {
        setFrameworks((prev) =>
          prev.map((f) =>
            f.id === framework.id ? { ...f, enabled: framework.enabled ? 0 : 1 } : f
          )
        )
      }
    } catch (err) {
      console.error('Failed to toggle framework:', err)
    } finally {
      setToggling(null)
    }
  }

  const handleShowVersions = async (framework: Framework) => {
    if (expandedVersions === framework.id) {
      setExpandedVersions(null)
      setVersions([])
      return
    }

    setExpandedVersions(framework.id)
    setLoadingVersions(true)
    try {
      const res = await api.getFrameworkVersions(framework.id)
      if (res.success) {
        setVersions(res.versions)
      }
    } catch (err) {
      console.error('Failed to load versions:', err)
    } finally {
      setLoadingVersions(false)
    }
  }

  const handleOpenUpgrade = (framework: Framework) => {
    setSelectedFramework(framework)
    setUpgradeForm({
      new_version: '',
      new_display_name: '',
      migration_notes: '',
      copy_controls: true,
    })
    setShowUpgradeModal(true)
  }

  const handleUpgrade = async () => {
    if (!selectedFramework || !upgradeForm.new_version || !upgradeForm.new_display_name) return

    setUpgrading(true)
    try {
      const res = await api.createFrameworkVersion(selectedFramework.id, {
        new_version: upgradeForm.new_version,
        new_display_name: upgradeForm.new_display_name,
        migration_notes: upgradeForm.migration_notes || undefined,
        copy_controls: upgradeForm.copy_controls,
      })

      if (res.success) {
        setShowUpgradeModal(false)
        fetchFrameworks()
        // Refresh versions if expanded
        if (expandedVersions === selectedFramework.id) {
          const versRes = await api.getFrameworkVersions(selectedFramework.id)
          if (versRes.success) {
            setVersions(versRes.versions)
          }
        }
      }
    } catch (err) {
      console.error('Failed to create new version:', err)
    } finally {
      setUpgrading(false)
    }
  }

  const handleOpenMigrate = (framework: Framework) => {
    if (!framework.superseded_by) return
    setSelectedFramework(framework)
    setMigrateForm({
      copy_evidence: true,
      copy_policies: true,
    })
    setShowMigrateModal(true)
  }

  const handleMigrate = async () => {
    if (!selectedFramework || !selectedFramework.superseded_by) return

    setMigrating(true)
    try {
      const res = await api.migrateFramework(selectedFramework.id, {
        target_framework_id: selectedFramework.superseded_by,
        copy_evidence: migrateForm.copy_evidence,
        copy_policies: migrateForm.copy_policies,
      })

      if (res.success) {
        setShowMigrateModal(false)
        fetchFrameworks()
      }
    } catch (err) {
      console.error('Failed to migrate framework:', err)
    } finally {
      setMigrating(false)
    }
  }

  const handleExport = async (framework: Framework) => {
    setExporting(framework.id)
    try {
      const res = await api.exportFramework(framework.id)
      if (res.success && res.export) {
        // Create and download JSON file
        const blob = new Blob([JSON.stringify(res.export, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${framework.name.replace(/\s+/g, '-').toLowerCase()}-export.json`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      }
    } catch (err) {
      console.error('Failed to export framework:', err)
    } finally {
      setExporting(null)
    }
  }

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setImportFile(file)
      setImportResult(null)
    }
  }

  const handleImport = async () => {
    if (!importFile) return

    setImporting(true)
    setImportResult(null)
    try {
      const text = await importFile.text()
      const exportData = JSON.parse(text)
      const res = await api.importFramework(exportData, importMode)

      setImportResult({
        success: res.success,
        message: res.message,
        statistics: res.statistics,
      })

      if (res.success) {
        fetchFrameworks()
      }
    } catch (err) {
      console.error('Failed to import framework:', err)
      setImportResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to parse import file',
      })
    } finally {
      setImporting(false)
    }
  }

  const resetImportModal = () => {
    setShowImportModal(false)
    setImportFile(null)
    setImportMode('create_new')
    setImportResult(null)
  }

  // formatDate is now provided by useDatePreferences hook

  const frameworkDescriptions: Record<string, string> = {
    'pci-dss': 'Payment Card Industry Data Security Standard - Required for organizations handling credit card data',
    'soc2': 'Service Organization Control 2 - Trust Services Criteria for service providers',
    'iso27001': 'Information Security Management System - International security standard',
    'hipaa': 'Health Insurance Portability and Accountability Act - Healthcare data protection',
    'sox': 'Sarbanes-Oxley Act - Financial reporting and internal controls for public companies',
    'CIS Controls': 'CIS Controls v8 - Prioritized cybersecurity best practices from the Center for Internet Security',
    'CMMC': 'Cybersecurity Maturity Model Certification - Required for DoD contractors handling CUI',
    'NIST 800-53': 'NIST 800-53 Rev 5 - Comprehensive security controls for federal systems and FedRAMP',
  }

  const frameworkColors: Record<string, string> = {
    'pci-dss': 'bg-blue-100 border-blue-300',
    'soc2': 'bg-purple-100 border-purple-300',
    'iso27001': 'bg-green-100 border-green-300',
    'hipaa': 'bg-red-100 border-red-300',
    'sox': 'bg-amber-100 border-amber-300',
    'CIS Controls': 'bg-cyan-100 border-cyan-300',
    'CMMC': 'bg-indigo-100 border-indigo-300',
    'NIST 800-53': 'bg-orange-100 border-orange-300',
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Compliance Frameworks</h1>
          <p className="text-gray-500">Enable or disable frameworks to manage which controls appear in your workspace</p>
        </div>
        {user?.role === 'admin' && (
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Upload className="h-4 w-4" />
            Import Framework
          </button>
        )}
      </div>

      {user?.role !== 'admin' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
          <Shield className="h-5 w-5 inline mr-2" />
          Only administrators can enable or disable frameworks.
        </div>
      )}

      <div className="grid gap-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : frameworks.length === 0 ? (
          <div className="text-center py-12 card">
            <Layers className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No frameworks available</p>
          </div>
        ) : (
          frameworks.map((framework) => (
            <div key={framework.id}>
              <div
                className={`card p-6 border-2 ${
                  framework.enabled
                    ? frameworkColors[framework.name] || frameworkColors[framework.base_framework_name || ''] || 'bg-gray-50 border-gray-300'
                    : 'bg-gray-50 border-gray-200 opacity-75'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {framework.display_name}
                      </h3>
                      {framework.version && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-white border rounded">
                          v{framework.version}
                        </span>
                      )}
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          framework.enabled
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {framework.enabled ? 'Active' : 'Inactive'}
                      </span>
                      {framework.is_latest ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
                          Latest
                        </span>
                      ) : null}
                      {framework.upgrade_available && (
                        <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-700 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          Upgrade Available
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 mt-1">
                      {framework.description || frameworkDescriptions[framework.name] || frameworkDescriptions[framework.base_framework_name || ''] || 'Compliance framework'}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      <span>{framework.control_count || 0} controls</span>
                      {framework.effective_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Effective: {formatDate(framework.effective_date)}
                        </span>
                      )}
                      {framework.sunset_date && (
                        <span className="flex items-center gap-1 text-orange-600">
                          <Clock className="h-3 w-3" />
                          Sunset: {formatDate(framework.sunset_date)}
                        </span>
                      )}
                    </div>
                    {framework.superseding_framework_name && (
                      <p className="text-sm text-orange-600 mt-2 flex items-center gap-1">
                        <ArrowUp className="h-3 w-3" />
                        Newer version available: {framework.superseding_framework_name}
                        {framework.superseding_framework_version && ` v${framework.superseding_framework_version}`}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {/* View Dashboard Button - visible to all users for enabled frameworks */}
                    {framework.enabled ? (
                      <Link
                        to={`/frameworks/${framework.id}/dashboard`}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-primary-600 bg-primary-50 hover:bg-primary-100 transition-colors text-sm font-medium"
                        title="View framework dashboard"
                      >
                        <BarChart2 className="h-4 w-4" />
                        <span className="hidden sm:inline">View Dashboard</span>
                      </Link>
                    ) : null}

                    {user?.role === 'admin' && (
                      <>
                        {/* Version History Button */}
                        <button
                          onClick={() => handleShowVersions(framework)}
                          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                          title="View version history"
                        >
                          {expandedVersions === framework.id ? (
                            <ChevronUp className="h-5 w-5" />
                          ) : (
                            <History className="h-5 w-5" />
                          )}
                        </button>

                        {/* Export Framework Button */}
                        <button
                          onClick={() => handleExport(framework)}
                          disabled={exporting === framework.id}
                          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors disabled:opacity-50"
                          title="Export framework to JSON"
                        >
                          {exporting === framework.id ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400" />
                          ) : (
                            <Download className="h-5 w-5" />
                          )}
                        </button>

                        {/* Create New Version Button */}
                        <button
                          onClick={() => handleOpenUpgrade(framework)}
                          className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                          title="Create new version"
                        >
                          <GitBranch className="h-5 w-5" />
                        </button>

                        {/* Migrate to New Version Button */}
                        {framework.upgrade_available && (
                          <button
                            onClick={() => handleOpenMigrate(framework)}
                            className="p-2 rounded-lg text-orange-600 hover:bg-orange-50 transition-colors"
                            title="Migrate to newer version"
                          >
                            <ArrowRight className="h-5 w-5" />
                          </button>
                        )}

                        {/* Toggle Button */}
                        <button
                          onClick={() => handleToggle(framework)}
                          disabled={toggling === framework.id}
                          className={`p-2 rounded-lg transition-colors ${
                            framework.enabled
                              ? 'text-green-600 hover:bg-green-100'
                              : 'text-gray-400 hover:bg-gray-100'
                          }`}
                        >
                          {toggling === framework.id ? (
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-current" />
                          ) : framework.enabled ? (
                            <ToggleRight className="h-8 w-8" />
                          ) : (
                            <ToggleLeft className="h-8 w-8" />
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Version History Panel */}
              {expandedVersions === framework.id && (
                <div className="ml-4 mt-2 border-l-2 border-gray-200 pl-4 space-y-2">
                  {loadingVersions ? (
                    <div className="flex items-center gap-2 text-gray-500 py-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" />
                      Loading versions...
                    </div>
                  ) : versions.length === 0 ? (
                    <p className="text-gray-500 py-2">No version history available</p>
                  ) : (
                    versions.map((ver, idx) => (
                      <div
                        key={ver.id}
                        className={`p-3 rounded-lg border ${
                          ver.id === framework.id
                            ? 'bg-blue-50 border-blue-200'
                            : 'bg-white border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{ver.display_name}</span>
                            {ver.version && (
                              <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                                v{ver.version}
                              </span>
                            )}
                            {ver.is_latest ? (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                Latest
                              </span>
                            ) : null}
                            {ver.id === framework.id && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                Current
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {ver.control_count} controls
                            {ver.compliant_count > 0 && (
                              <span className="text-green-600 ml-2">
                                ({ver.compliant_count} compliant)
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                          {ver.effective_date && (
                            <span>Effective: {formatDate(ver.effective_date)}</span>
                          )}
                          {ver.sunset_date && (
                            <span className="text-orange-500">Sunset: {formatDate(ver.sunset_date)}</span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="card p-4 bg-blue-50 border border-blue-200">
        <h4 className="font-medium text-blue-900">About Framework Versions</h4>
        <p className="text-sm text-blue-700 mt-1">
          Framework version management allows you to track and manage different versions of compliance frameworks
          (e.g., PCI DSS 3.2.1 to 4.0). When a new version is released, you can create it, copy existing controls,
          and then migrate evidence and policies from the old version to the new one.
        </p>
      </div>

      {/* Create New Version Modal */}
      {showUpgradeModal && selectedFramework && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Create New Framework Version</h3>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-gray-600 text-sm">
                Creating a new version of <strong>{selectedFramework.display_name}</strong>.
                This will link the old version to the new one for migration tracking.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Version Number *
                </label>
                <input
                  type="text"
                  value={upgradeForm.new_version}
                  onChange={(e) => setUpgradeForm({ ...upgradeForm, new_version: e.target.value })}
                  placeholder="e.g., 4.0.1"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name *
                </label>
                <input
                  type="text"
                  value={upgradeForm.new_display_name}
                  onChange={(e) => setUpgradeForm({ ...upgradeForm, new_display_name: e.target.value })}
                  placeholder="e.g., PCI DSS 4.0.1"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Migration Notes
                </label>
                <textarea
                  value={upgradeForm.migration_notes}
                  onChange={(e) => setUpgradeForm({ ...upgradeForm, migration_notes: e.target.value })}
                  placeholder="Notes about changes in this version..."
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="copy_controls"
                  checked={upgradeForm.copy_controls}
                  onChange={(e) => setUpgradeForm({ ...upgradeForm, copy_controls: e.target.checked })}
                  className="h-4 w-4 text-blue-600 rounded"
                />
                <label htmlFor="copy_controls" className="text-sm text-gray-700">
                  Copy existing controls to new version
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleUpgrade}
                disabled={upgrading || !upgradeForm.new_version || !upgradeForm.new_display_name}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {upgrading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    Create Version
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Migrate Framework Modal */}
      {showMigrateModal && selectedFramework && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Migrate to New Version</h3>
              <button
                onClick={() => setShowMigrateModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-orange-800 font-medium">Migration Warning</p>
                    <p className="text-orange-700 text-sm mt-1">
                      This will migrate evidence and policies from <strong>{selectedFramework.display_name}</strong>
                      {' '}to <strong>{selectedFramework.superseding_framework_name}</strong>.
                      The old framework will be disabled after migration.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="copy_evidence"
                  checked={migrateForm.copy_evidence}
                  onChange={(e) => setMigrateForm({ ...migrateForm, copy_evidence: e.target.checked })}
                  className="h-4 w-4 text-blue-600 rounded"
                />
                <label htmlFor="copy_evidence" className="text-sm text-gray-700">
                  Migrate evidence to matching controls
                </label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="copy_policies"
                  checked={migrateForm.copy_policies}
                  onChange={(e) => setMigrateForm({ ...migrateForm, copy_policies: e.target.checked })}
                  className="h-4 w-4 text-blue-600 rounded"
                />
                <label htmlFor="copy_policies" className="text-sm text-gray-700">
                  Migrate policy links to matching controls
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowMigrateModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleMigrate}
                disabled={migrating}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
              >
                {migrating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Migrating...
                  </>
                ) : (
                  <>
                    <ArrowRight className="h-4 w-4" />
                    Start Migration
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Framework Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <FileJson className="h-5 w-5 text-primary-600" />
                Import Framework
              </h3>
              <button
                onClick={resetImportModal}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-gray-600 text-sm">
                Import a framework from a previously exported JSON file. The import will create
                a new framework with all controls included in the export.
              </p>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Export File
                </label>
                <div className="flex items-center justify-center w-full">
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 border-gray-300">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      {importFile ? (
                        <>
                          <FileJson className="h-8 w-8 text-primary-600 mb-2" />
                          <p className="text-sm text-gray-700 font-medium">{importFile.name}</p>
                          <p className="text-xs text-gray-500">
                            {(importFile.size / 1024).toFixed(1)} KB
                          </p>
                        </>
                      ) : (
                        <>
                          <Upload className="h-8 w-8 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500">
                            <span className="font-medium">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-gray-400">JSON file only</p>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      accept=".json,application/json"
                      onChange={handleImportFile}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {/* Import Mode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Import Mode
                </label>
                <div className="space-y-2">
                  <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="import_mode"
                      value="create_new"
                      checked={importMode === 'create_new'}
                      onChange={() => setImportMode('create_new')}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Create New</p>
                      <p className="text-sm text-gray-500">
                        Always create a new framework, even if one with the same name exists
                      </p>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="import_mode"
                      value="merge"
                      checked={importMode === 'merge'}
                      onChange={() => setImportMode('merge')}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Merge</p>
                      <p className="text-sm text-gray-500">
                        Add new controls to existing framework, skip controls that already exist
                      </p>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="import_mode"
                      value="replace"
                      checked={importMode === 'replace'}
                      onChange={() => setImportMode('replace')}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Replace</p>
                      <p className="text-sm text-gray-500">
                        Update existing controls with imported data, add new controls
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Import Result */}
              {importResult && (
                <div
                  className={`p-4 rounded-lg ${
                    importResult.success
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-red-50 border border-red-200'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {importResult.success ? (
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
                    )}
                    <div>
                      <p className={importResult.success ? 'text-green-800' : 'text-red-800'}>
                        {importResult.message}
                      </p>
                      {importResult.statistics && (
                        <div className="mt-2 text-sm">
                          <p className="text-green-700">
                            Controls created: {importResult.statistics.controls_created}
                          </p>
                          {importResult.statistics.controls_updated > 0 && (
                            <p className="text-blue-700">
                              Controls updated: {importResult.statistics.controls_updated}
                            </p>
                          )}
                          {importResult.statistics.controls_skipped > 0 && (
                            <p className="text-gray-600">
                              Controls skipped: {importResult.statistics.controls_skipped}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
              <button
                onClick={resetImportModal}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                {importResult?.success ? 'Close' : 'Cancel'}
              </button>
              {!importResult?.success && (
                <button
                  onClick={handleImport}
                  disabled={importing || !importFile}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {importing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      Import
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
